import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	QueryList,
	ContentChildren,
	WritableSignal,
	signal,
	AfterViewInit,
	HostListener
} from '@angular/core';

import { VbUiSidebarButtonComponent } from './VbUiSidebarButton.Component';

import styles from './VbUiSidebar.Component.module.less';
import { focusNextTarget } from 'rev-shared/util/FocusHelper';
import { getMobileSidebarStyles } from './VbUiSidebarLayoutMobileStyles';
import { IRules } from '../css/CssRules.Contract';

export interface ISidebarToggleEvent {
	activeSidebarButtonId: string;
	isOpen: boolean;
}

export enum VbUiSidebarSlot {
	BUTTONS = 'sidebarButtons',
	HEADER_CONTROLS = 'headerControls',
	PANEL_BODY = 'panelBody'
}

@Component({
	selector: 'vb-ui-sidebar',
	templateUrl: './VbUiSidebar.Component.html',
	host: { // layout attribute is specified below with a HostBinding
		'[class]': 'hostClass',
		'[class.vbUiSidebarRootOpen]': 'isOpen()'
	},
	standalone: false
})
export class VbUiSidebarComponent implements OnChanges, OnInit, AfterViewInit {
	@Input() public defaultClickedButtonId: string;
	@Input() public closeBtnHidden?: boolean;
	@Input() public mobileLayoutBreakPointSize: number;
	@Input() public smallButtons: boolean;
	@Input() public disableMobileLayout: boolean;
	@Input() public openWidthPx: number;
	@Input() public themed: boolean;
	@Input() public noBorder: boolean;
	@Input() public hasLowerContent: boolean;
	@Output() public onToggle = new EventEmitter<ISidebarToggleEvent>();

	@ContentChildren(VbUiSidebarButtonComponent, { descendants: true }) public sidebarButtons: QueryList<VbUiSidebarButtonComponent>;

	public readonly activeButton: WritableSignal<VbUiSidebarButtonComponent> = signal(null);
	public readonly isOpen: WritableSignal<boolean> = signal(false);
	public isMobileWindow: boolean;

	private openedOnce: boolean = false;
	public hostClass: string;
	public enableAnimation: boolean;
	public cssRules: IRules;
	private isKeyboardControlled: boolean;

	public readonly styles = styles;

	public ngOnInit(): void {
		setTimeout(() => {
			this.enableAnimation = true;
		}, 500);
	}

	public ngAfterViewInit(): void {
		this.sidebarButtons.changes.subscribe((queryList: QueryList<VbUiSidebarButtonComponent>) => {
			const activeButton = this.activeButton()?.id;
			if(activeButton && queryList.toArray().every(b => b.id !== activeButton)) {
				this.closeSidebar();
			}
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if(!this.openedOnce && changes.defaultClickedButtonId) {
			this.openDefaultSidebarPanel();
		}
		this.updateHostClass();
	}

	@HostListener('document:keydown', ['$event'])
	public onKeydown(e: KeyboardEvent): void {
		if(e.key === 'Tab') {
			//Prevent focus by clicking empty space from opening the sidebar
			this.isKeyboardControlled = true;
		}
	}

	@HostListener('document:keyup', ['$event'])
	public onKeyup(e: KeyboardEvent): void {
		if(e.key === 'Tab') {
			this.isKeyboardControlled = false;
		}
	}

	public updateHostClass(): void {
		this.hostClass = [
			this.styles.root,
			this.themed ? this.styles.themed : '',
			this.hasLowerContent ? this.styles.fixedMobileScroll : '',
			this.smallButtons ? this.styles.mobileSidebar : ''
		].join(' ');

		this.cssRules = this.disableMobileLayout ? null :
			getMobileSidebarStyles(this.mobileLayoutBreakPointSize);
	}

	public onFocusButtons($event: FocusEvent): void {
		if(!this.isKeyboardControlled) {
			return;
		}

		const activeButton = this.activeButton()?.button.nativeElement;
		if($event.relatedTarget === activeButton) {
			return;
		}

		if(this.isOpen()) {
			activeButton.focus();
			return;
		}

		console.log('focus sidebar buttons container');
		this.moveFocus();
	}

	public moveFocus(reverse?: boolean): void {
		const buttons = this.sidebarButtons.map(b => b.button.nativeElement);
		const selected = focusNextTarget(
			buttons.filter(b => b.matches(':not([disabled])') && b.checkVisibility()),
			reverse);
		const index = buttons.indexOf(selected as HTMLButtonElement);
		const buttonCmp = this.sidebarButtons.get(index);
		console.log('moving focus to ', index, buttonCmp?.id);
		if (buttonCmp) {
			//only open sidebar on focus when there is a panel to show, otherwise just focus the button.
			if (!buttonCmp.action?.observed) {
				this.openSidebar(buttonCmp.id);
			}
		}
	}

	private openDefaultSidebarPanel(): void {
		if(!this.defaultClickedButtonId) {
			return;
		}

		setTimeout(() => {
			if(!this.defaultClickedButtonId || !this.sidebarButtons.find(b => b.id === this.defaultClickedButtonId)) {
				return;
			}

			this.openSidebar(this.defaultClickedButtonId);
		}, 200);
	}

	public onClick(buttonId: string): void {
		if(this.isOpen() && this.activeButton()?.id === buttonId) {
			this.closeSidebar();
		}
		else {
			this.openSidebar(buttonId);
		}
	}

	public closeSidebar(): void {
		this.activeButton.set(undefined);
		this.toggleSidebar(false);
	}

	public openSidebar(id: string): void {
		const sidebarButton = this.sidebarButtons.find(b => b.id === id);
		this.activeButton.set(sidebarButton);
		this.toggleSidebar(true);
	}

	private toggleSidebar(isOpen: boolean): void {
		this.isOpen.set(!!isOpen);
		this.openedOnce = this.openedOnce || isOpen;
		this.onToggle.emit({
			activeSidebarButtonId: this.activeButton()?.id,
			isOpen
		});
	}

	public getNotificationCountDisplay(count: number): number | string {
		return count <= 99 ? count : '99+';
	}
}
